import { type ReactNode, useMemo } from 'react';

import type {
  ICustomFrontIcon,
  IRenderTreeData,
} from '@components/common/TreeView/types';

import type { SxProps } from '@mui/material';
import type { Theme } from '@mui/material/styles';

import { CustomTreeItem } from '@components';

interface ITreeNodesParameters {
  disabled: boolean;
  withCopy: boolean;
  data: IRenderTreeData[];
  itemSx?: SxProps<Theme>;
  customFrontIcon?: null | ICustomFrontIcon;
  renderCustomFrontIcon: (node: IRenderTreeData) => null | ICustomFrontIcon;
}

export const useTreeNodes = ({
  data,
  itemSx,
  disabled,
  withCopy,
  customFrontIcon,
  renderCustomFrontIcon,
}: ITreeNodesParameters): ReactNode[] => {
  const renderTree = useMemo(() => {
    const renderNodes = (nodes: IRenderTreeData[]): ReactNode[] =>
      nodes.map((node) => (
        <CustomTreeItem
          customFrontIcon={
            customFrontIcon ? (renderCustomFrontIcon?.(node) ?? null) : null
          }
          disabled={disabled}
          itemId={String(node.id)}
          key={node.id}
          labelText={node.name}
          sx={itemSx}
          withCopy={withCopy}
        >
          {Array.isArray(node.children) ? renderNodes(node.children) : null}
        </CustomTreeItem>
      ));

    return renderNodes(data);
  }, [
    data,
    disabled,
    withCopy,
    itemSx,
    customFrontIcon,
    renderCustomFrontIcon,
  ]);

  return renderTree;
};
